import {
  Actions,
  BoltIcon,
  Breadcrumb,
  Button,
  ErrorAlert,
  Input,
  Modal,
  modalHandler,
  ModalOpenBtn,
  PageHero,
  Progress,
  success,
  SwitchInput,
  Table,
  UploadImageCard,
  UploadVideoCard,
  useCanShow,
  useCustomForm,
  useDelete,
  useFetchData,
  useState,
  useTableColumns,
  VideoLargePreview,
} from "@";
import { defaultValues, schema } from "./utils/validations";
import { getFastDonations, saveFastDonation } from "./utils/api";
import { tableColumns, TablePreview } from "./components/tableColumns.jsx";

const Index = () => {
  // Breadcrumb links
  const links = [{ name: "التبرع السريع", link: "" }];
  // State to determine if the form is in edit mode
  const [isEdit, setIsEdit] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);

  // Table columns configuration
  const columns = useTableColumns(
    [
      {
        name: "العرض",
        grow: 1,
        cell: (row) => (
          <TablePreview row={row} setVideoPreview={setVideoPreview} />
        ),
      },
      ...tableColumns,
    ],
    (row) => (
      <>
        {useCanShow("editMainBanner") && (
          <Actions
            notShow
            row={row}
            edit={handlerEdit}
            delete={deleteHandler}
          />
        )}
      </>
    ),
  );

  // Form methods from useCustomForm hook
  const {
    watch,
    reset,
    errors,
    register,
    setValue,
    getValues,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(defaultValues, schema);

  // Data fetching methods from useFetchData hook
  const { data, setData, isLoading, error, fetchData } = useFetchData({
    noPgn: true,
    fun: getFastDonations,
  });

  // Delete handler from useDelete hook
  const { deleteHandler } = useDelete(data, setData, "fastDonations");

  // Function to clear the form and reset edit state
  const clearForm = () => {
    setIsEdit(false);
    reset(defaultValues);
  };

  // Function to handle form submission
  const submit = async () => {
    try {
      await saveFastDonation(getValues());
      await fetchData();
      success("تم الحفظ بنجاح");
      modalHandler();
      clearForm();
    } catch (error) {}
  };

  // Function to handle editing of carousel items
  const handlerEdit = (item) => {
    const data = {
      ...item,
      file: null,
      mediaType: item.mediaType.toString(),
      fileUrl: item.fullUrl,
    };
    reset(data);
    setIsEdit(true);
    modalHandler();
  };

  return (
    <>
      {/* Error alert */}
      <ErrorAlert reload={fetchData} show={error} />
      {/* Breadcrumb navigation */}
      <Breadcrumb links={links} />

      {/* Video preview popup */}
      <VideoLargePreview
        url={videoPreview}
        video={videoPreview}
        hide={() => setVideoPreview(false)}
      />

      {/* Modal for adding/editing carousel items */}
      <Modal close={clearForm} title={isEdit ? "تعديل" : "اضافة"}>
        <form
          onSubmit={handleSubmit(submit)}
          className="grid gap-5 p-2 md:grid-cols-2 md:p-5"
        >
          {/* Input for banner name */}
          <Input
            label="العنوان"
            error={errors.title}
            input={register("title")}
          />

          {/* Input for banner order */}
          <Input
            star
            label="الترتيب"
            error={errors.order}
            input={register("order")}
          />

          {/* MediaType selector */}
          <div className="flex items-center gap-10">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                className="input w-5 rounded-full dark:checked:bg-purple-600"
                value="0"
                {...register("mediaType")}
              />
              <span>صورة</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                className="input w-5 rounded-full dark:checked:bg-purple-600"
                value="1"
                {...register("mediaType")}
              />
              <span>فيديو</span>
            </label>
          </div>

          {/* Checkbox for active status */}
          <SwitchInput
            label="نشط"
            checked={watch("isActive")}
            change={(value) => setValue("isActive", value)}
          />

          {/* Upload image card for banner image - shown when mediaType is 0 */}
          {watch("mediaType") === "0" && (
            <div className="md:col-span-2">
              <UploadImageCard
                star
                show={true}
                title="اختر الصورة"
                src={getValues("fileUrl")}
                success={isSubmitSuccessful}
                error={errors.file?.message}
                upload={(file) => setValue("file", file)}
              />
            </div>
          )}

          {/* Video URL input - shown when mediaType is 1 */}
          {watch("mediaType") === "1" && (
            <div className="md:col-span-2">
              <UploadVideoCard
                star
                show={true}
                title="اختر الفيديو"
                src={getValues("fileUrl")}
                success={isSubmitSuccessful}
                error={errors.file?.message}
                upload={(file) => setValue("file", file)}
              />
            </div>
          )}

          {/* Submit button */}
          <div className="flex flex-wrap items-center justify-start md:col-span-2">
            <Button
              isLoading={isSubmitting}
              type="submit"
              title="حفظ البيانات"
            />
            <Progress show={isSubmitting} />
          </div>
        </form>
      </Modal>

      <main className="table-card relative">
        <PageHero
          eyebrow="الموقع / التبرع السريع"
          title="التبرع السريع"
          count={data?.length}
          icon={BoltIcon}
          hideCount={isLoading}
          headerClassName="flex-row items-center justify-between"
        >
          {useCanShow("editMainBanner") && <ModalOpenBtn title="اضافة" />}
        </PageHero>

        {/* Table for displaying carousel items */}
        <Table localPg data={data} columns={columns} isLoading={isLoading} />
      </main>
    </>
  );
};

export default Index;
